import { useEffect, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { uniqueChannelName } from '../../lib/realtime';
import { supabase } from '../../lib/supabase';
import { Switch } from '../ui';

type BreakerRow = {
  id: number;
  is_on: boolean;
  updated_at: string;
};

const BREAKER_HINT =
  'Off finishes the current job and does not take the next one. On wakes Owlbert locally. Telegram stays for talking.';

/** Compact queue breaker — label + switch, lives in the Agents sub-header. */
export default function QueueBreakerToggle() {
  const { user } = useUser();
  const [isOn, setIsOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const { data, error: err } = await supabase
      .from('queue_breaker')
      .select('id, is_on, updated_at')
      .eq('id', 1)
      .maybeSingle();
    if (err) throw err;
    if (data) setIsOn(Boolean((data as BreakerRow).is_on));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await load();
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading queue breaker:', err);
          setError('Breaker unavailable');
        }
      }
    })();
    const channel = supabase
      .channel(uniqueChannelName('queue_breaker'))
      .on('postgres_changes', { event: '*', schema: 'public', table: 'queue_breaker' }, () => {
        load().catch((e) => console.error('Error refreshing queue breaker:', e));
      })
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  const handleChange = async (next: boolean) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const prev = isOn;
    setIsOn(next);
    try {
      const { error: err } = await supabase
        .from('queue_breaker')
        .update({
          is_on: next,
          updated_by: user?.id ?? null,
        })
        .eq('id', 1);
      if (err) throw err;
    } catch (err) {
      console.error('Error updating queue breaker:', err);
      setIsOn(prev);
      setError('Could not flip the breaker');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      title={BREAKER_HINT}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}
    >
      {error ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--danger-400)' }}>{error}</span>
      ) : null}
      <span className="t-eyebrow" style={{ color: 'var(--text-faint)' }}>
        Queue breaker
      </span>
      <Switch
        checked={isOn}
        disabled={busy}
        onChange={handleChange}
        label={
          <span
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
              color: isOn ? 'var(--text-body)' : 'var(--text-muted)',
              minWidth: '24px',
            }}
          >
            {isOn ? 'On' : 'Off'}
          </span>
        }
      />
    </div>
  );
}
